import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { applyBulkImport, exportBulkCsv, listBulkGroups, previewBulkImport } from '../api/bulk';
import { ApiError } from '../api/client';
import type { BulkGroup, BulkPreview } from '../api/types';
import { useCurrentUser } from '../auth/CurrentUserProvider';
import { BulkImportHistory } from '../components/BulkImportHistory';
import { BulkPreviewTable } from '../components/BulkPreviewTable';
import { useDialog } from '../components/Dialog';
import { Dropdown } from '../components/Dropdown';
import { ErrorBanner } from '../components/ErrorBanner';
import { useSetPageTitle } from '../components/PageTitle';
import { useToast } from '../components/Toast';
import { saveBlob } from '../download';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

export function BulkEditPage() {
  const { t } = useTranslation();
  const toast = useToast();
  const dialog = useDialog();
  const { user, loading: identifying } = useCurrentUser();
  useSetPageTitle(t('bulk.title'));

  const [groups, setGroups] = useState<BulkGroup[]>([]);
  const [groupId, setGroupId] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [showUnchanged, setShowUnchanged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [stale, setStale] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [historyKey, setHistoryKey] = useState(0);
  const fileInput = useRef<HTMLInputElement>(null);

  const allowed = user?.canBulkEdit === true;

  useEffect(() => {
    if (!allowed) {
      return;
    }

    listBulkGroups()
      .then((found) => {
        setGroups(found);
        setGroupId((current) => current || found[0]?.id || '');
      })
      .catch(setError);
  }, [allowed]);

  const group = groups.find((candidate) => candidate.id === groupId);

  const shownRows = useMemo(
    () => (preview?.rows ?? []).filter((row) => showUnchanged || row.action !== 'Unchanged'),
    [preview, showUnchanged],
  );

  const toApply = preview ? preview.counts.create + preview.counts.update : 0;

  function reset() {
    setFile(null);
    setPreview(null);
    setStale(false);
    if (fileInput.current) {
      fileInput.current.value = '';
    }
  }

  function changeGroup(next: string) {
    setGroupId(next);
    reset();
  }

  async function handleExport() {
    setBusy(true);
    try {
      saveBlob(await exportBulkCsv(groupId), `qr-codes-${group?.name ?? 'group'}-${today()}.csv`);
      setError(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function handleFile(chosen: File | null) {
    setFile(chosen);
    setPreview(null);
    setStale(false);

    if (!chosen) {
      return;
    }

    setBusy(true);
    try {
      setPreview(await previewBulkImport(groupId, chosen));
      setError(null);
    } catch (caught) {
      setError(caught);
    } finally {
      setBusy(false);
    }
  }

  async function handleApply() {
    if (!preview || !file) {
      return;
    }

    const confirmed = await dialog.confirm({
      title: t('bulk.confirmTitle', { count: toApply }),
      body: (
        <>
          <ul className="mb-2">
            <li>{t('bulk.confirmCreate', { count: preview.counts.create })}</li>
            <li>{t('bulk.confirmUpdate', { count: preview.counts.update })}</li>
            {preview.counts.error > 0 && <li>{t('bulk.errorsSkipped', { count: preview.counts.error })}</li>}
          </ul>
          <p className="mb-0">{t('bulk.confirmBody', { group: group?.name ?? '' })}</p>
        </>
      ),
      confirmLabel: t('bulk.apply', { count: toApply }),
      tone: 'primary',
      icon: 'pi-check-circle',
    });

    if (!confirmed) {
      return;
    }

    setBusy(true);
    const dismissProgress = toast.show({
      message: t('bulk.applying', { count: toApply }),
      tone: 'info',
      icon: 'pi-spin pi-spinner',
      duration: null,
    });

    try {
      const applied = await applyBulkImport(groupId, preview.fingerprint, file);
      setHistoryKey((key) => key + 1);
      reset();
      setError(null);
      toast.success(t('bulk.applied', { created: applied.counts.create, updated: applied.counts.update }));
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 409 && caught.problem?.preview) {
        setPreview(caught.problem.preview);
        setStale(true);
        setError(null);
      } else {
        setError(caught);
      }
    } finally {
      dismissProgress();
      setBusy(false);
    }
  }

  if (identifying) {
    return (
      <p>
        <i className="pi pi-spin pi-spinner me-2" aria-hidden="true" />
        {t('app.loading')}
      </p>
    );
  }

  if (!allowed) {
    return (
      <div className="alert alert-info d-flex gap-2" role="status">
        <i className="pi pi-lock mt-1" aria-hidden="true" />
        <span>{t('bulk.notAllowed')}</span>
      </div>
    );
  }

  return (
    <>
      <ErrorBanner error={error} />

      <div className="card">
        <h2>
          <i className="pi pi-file-import me-2" aria-hidden="true" />
          {t('bulk.title')}
        </h2>
        <p className="hint">{t('bulk.intro')}</p>

        <div className="toolbar">
          <div className="field">
            <label htmlFor="bulk-group" id="bulk-group-label">
              <i className="pi pi-users me-1" aria-hidden="true" />
              {t('common.group')}
            </label>
            <Dropdown
              id="bulk-group"
              labelledBy="bulk-group-label"
              variant="field"
              value={groupId}
              options={groups.map((candidate) => ({
                value: candidate.id,
                label: t('bulk.groupOption', { name: candidate.name, count: candidate.qrCodeCount }),
              }))}
              onChange={changeGroup}
              disabled={busy || groups.length === 0}
            />
          </div>

          <button
            type="button"
            className="btn btn-outline-primary align-self-end"
            onClick={() => void handleExport()}
            disabled={busy || !groupId}
          >
            <i className="pi pi-download me-2" aria-hidden="true" />
            {t('bulk.export')}
          </button>

          <div className="field">
            <label htmlFor="bulk-file">
              <i className="pi pi-upload me-1" aria-hidden="true" />
              {t('bulk.import')}
            </label>
            <input
              id="bulk-file"
              ref={fileInput}
              type="file"
              accept=".csv,text/csv"
              className="form-control"
              disabled={busy || !groupId}
              onChange={(event) => void handleFile(event.target.files?.[0] ?? null)}
            />
          </div>
        </div>
      </div>

      {preview && (
        <div className="card">
          <div className="card-heading">
            <h2>
              <i className="pi pi-list-check me-2" aria-hidden="true" />
              {t('bulk.previewTitle')}
            </h2>
            <span className="badge bulk-badge--create">{t('bulk.countCreate', { count: preview.counts.create })}</span>
            <span className="badge bulk-badge--update">{t('bulk.countUpdate', { count: preview.counts.update })}</span>
            <span className="badge badge-neutral">{t('bulk.countUnchanged', { count: preview.counts.unchanged })}</span>
            <span className="badge bulk-badge--error">{t('bulk.countError', { count: preview.counts.error })}</span>
          </div>

          {stale && (
            <div className="alert alert-warning d-flex gap-2" role="alert">
              <i className="pi pi-refresh mt-1" aria-hidden="true" />
              <span>{t('bulk.stale')}</span>
            </div>
          )}

          {preview.fileErrors.map((fileError) => (
            <div key={fileError} className="alert alert-danger d-flex gap-2" role="alert">
              <i className="pi pi-times-circle mt-1" aria-hidden="true" />
              <span>{fileError}</span>
            </div>
          ))}

          {preview.rows.length > 0 && (
            <>
              <div className="form-check form-switch mb-2">
                <input
                  id="bulk-show-unchanged"
                  type="checkbox"
                  role="switch"
                  className="form-check-input"
                  checked={showUnchanged}
                  onChange={(event) => setShowUnchanged(event.target.checked)}
                />
                <label className="form-check-label" htmlFor="bulk-show-unchanged">
                  {t('bulk.showUnchanged', { count: preview.counts.unchanged })}
                </label>
              </div>

              {shownRows.length > 0 ? (
                <BulkPreviewTable rows={shownRows} />
              ) : (
                <p className="hint">{t('bulk.nothingChanges')}</p>
              )}
            </>
          )}

          <div className="d-flex flex-wrap align-items-center gap-2 mt-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => void handleApply()}
              disabled={busy || toApply === 0 || preview.fileErrors.length > 0}
            >
              <i className={`pi ${busy ? 'pi-spin pi-spinner' : 'pi-check'} me-2`} aria-hidden="true" />
              {t('bulk.apply', { count: toApply })}
            </button>
            <button type="button" className="btn btn-light" onClick={reset} disabled={busy}>
              {t('common.cancel')}
            </button>
            {preview.counts.error > 0 && (
              <span className="text-danger small">{t('bulk.errorsSkipped', { count: preview.counts.error })}</span>
            )}
          </div>
        </div>
      )}

      {groupId && (
        <BulkImportHistory groupId={groupId} refreshKey={historyKey} onError={setError} />
      )}
    </>
  );
}
